/**
 * Functions common for the frontend search pages
 *
 * (c) 2017 Alexander Schenkel, Kaden und Partner AG
 */

import historyManager from 'components/history-manager';
import { unique, toggleElement, addClass, removeClass } from 'components/utils';

function toggleClassForParam(el, cls, params, value) {
    if (!(params instanceof Array)) {
        params = [params];
    }
    if (params.indexOf(value) < 0) {
        removeClass(el, cls);
    } else {
        addClass(el, cls);
    }
}

function toggleCheckboxForParam(el, params, value) {
    if (el.type === 'checkbox') {
        if (!(params instanceof Array)) {
            params = [params];
        }
        el.checked = params.indexOf(value) >= 0;
    }
}

export function updateFilterToggle(field, paramValues, filterValue) {
    toggleClassForParam(field, 'active', paramValues, filterValue);
    toggleCheckboxForParam(field, paramValues, filterValue);
}

/**
 * Activates a DOM element as "toggle filter": It toggles a filter on or off on-click.
 * The DOM element may provide the following data-attributes for configuration:
 *
 * - data-filter: The name of the filter to apply
 * - data-value: The filter value to apply
 * - data-single-value: If true, the value is exclusive, if false, a on-toggle appends the value to the existing onw
 * - data-clear: If true, this element does CLEAR the named filter, instead of toggling a value (useful for 'clear' buttons)
 * - data-clear-params: A comma-separated list of filter names to be CLEARED when this filter applies (don't ask... read ticket #14362)
 *
 * @param {Element} field The DOM Element to activate the toggle filter for
 */
export function activateFilterToggle(field) {
    let params = historyManager.getUrlParams();

    // initial filter class toggle:
    let filterName = field.getAttribute('data-filter');
    let filterValue = field.getAttribute('data-value');
    let singleValue = field.getAttribute('data-single-value') === 'true';
    let clearValue = field.getAttribute('data-clear') === 'true';
    let paramValues = params[filterName] || [];
    // are there any params to CLEAR from the search when this filter applies?
    let clearParams = (field.getAttribute('data-clear-params') || '').split(',');

    historyManager.history.listen((location, action) => {
        let params = historyManager.getUrlParams();
        updateFilterToggle(field, params[filterName] || [], filterValue);
    });

    // initial toggle
    updateFilterToggle(field, paramValues, filterValue);

    field.addEventListener('click', (e) => {
        let params = historyManager.getUrlParams();
        let paramValues = params[filterName] || [];
        let el = e.target;
        if (el && el.type !== 'checkbox') {
            e.preventDefault();
        }

        // make an array from the value
        if (!(paramValues instanceof Array)) {
            paramValues = [paramValues];
        }
        // adds / removes the filter value from the param list. Make sure it contains
        // only one element if it is a single-value-param
        toggleElement(paramValues, filterValue);
        if (singleValue) {
            if (paramValues.length > 0) {
                paramValues = paramValues.filter((item) => item === filterValue);
            }
        }
        paramValues = unique(paramValues);
        params[filterName] = paramValues;

        // Remove the param completely if no value is set OR if this is teh clear value field:
        if (clearValue || params[filterName].length < 1) {
            delete params[filterName];
        }

        // clear out params when requested in the data-clear-params attributes:
        clearParams.forEach((clearParam) => {
            if (clearParam) {
                delete params[clearParam];
            }
        });

        // inform the history:
        historyManager.pushParams(params);
    });
}

/**
 * Adds ENTER key listener to the given input field, to start
 * search on enter. The given input field supports the following
 * additional data attributes for configuration:
 *
 * - data-filter: The name of the filter
 * - data-clear-params: A comma-separated list of filter names to be CLEARED when this filter applies (don't ask... read ticket #14362)
 *
 * @param {Element} field The field to apply the on-enter handler for
 */
export function activateFilterTextfield(field) {
    let params = historyManager.getUrlParams();
    // initial filter class toggle:
    let filterName = field.getAttribute('data-filter');
    let paramValue = params[filterName] || '';

    // are there any params to CLEAR from the search when this filter applies?
    let clearParams = (field.getAttribute('data-clear-params') || '').split(',');

    // initial value
    field.value = paramValue;

    historyManager.history.listen((location, action) => {
        let params = historyManager.getUrlParams();
        field.value = params[filterName] || '';
    });

    field.addEventListener('keypress', (e) => {
        if (e.keyCode === 13) {
            let params = historyManager.getUrlParams();
            let value = field.value;
            if (!value) {
                delete params[filterName];
            } else {
                params[filterName] = field.value;
            }

            // clear out params when requested in the data-clear-params attributes:
            clearParams.forEach((clearParam) => {
                if (clearParam) {
                    delete params[clearParam];
                }
            });
            // inform the history:
            historyManager.pushParams(params);
        }
    });
}
